import React, { useMemo, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  MenuItem,
  Select,
} from "@mui/material";
import { CartContext } from "../context/CartContext";
import { useCatalog } from "../hooks/useCatalog"; // <- fetches /api/catalog

const slugify = (s = "") =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const toMoney = (amount, currency = "cad") =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amount || 0) / 100);

export default function ProductDetailPage() {
  const { id } = useParams(); // expect route like /product/:id or /product/:slug
  const { products = [], loading, error } = useCatalog();
  const { addToCart } = useContext(CartContext);

  // Find product by Stripe product id OR by slug of its name
  const productRaw = useMemo(() => {
    if (!products?.length) return null;
    return (
      products.find((p) => p.id === id) ||
      products.find((p) => (p.slug || slugify(p.name)) === id) ||
      null
    );
  }, [products, id]);

  // Map Stripe product -> legacy shape your UI/cart expects
  const product = useMemo(() => {
    if (!productRaw) return null;

    // Build labels & price maps from Stripe prices
    const sizes = (productRaw.sizes || []).map((s) => s.label);
    const price = {};
    const priceIdBySize = {};
    const currencyBySize = {};

    (productRaw.sizes || []).forEach((s) => {
      const label = s.label;
      if (!label) return;
      price[label] = (Number(s.unitAmount || 0) / 100).toFixed(2);
      priceIdBySize[label] = s.priceId;
      currencyBySize[label] = s.currency;
    });

    // keep everything else (image/name/description/featured/spiceLevel)
    return {
      ...productRaw,
      sizes, // ["5oz","12oz"]
      price, // { "5oz":"7.99", ... }
      priceIdBySize, // { "5oz":"price_123", ... } (handy for CartContext)
      currencyBySize, // { "5oz":"cad", ... }
    };
  }, [productRaw]);

  const [selectedSize, setSelectedSize] = useState(product?.sizes?.[0] || "");

  // When product loads (async), ensure selectedSize is initialized
  React.useEffect(() => {
    if (product && product.sizes?.length && !selectedSize) {
      setSelectedSize(product.sizes[0]);
    }
  }, [product, selectedSize]);

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h5">Loading…</Typography>
      </Container>
    );
  }
  if (error || !product) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h5">Product not found</Typography>
      </Container>
    );
  }

  const handleAddToCart = () => {
    // Keep the same signature (product, selectedSize)
    // Your CartContext can use product.priceIdBySize[selectedSize] to send the correct Stripe Price ID
    addToCart(product, selectedSize);
  };

  const unitAmountCents =
    productRaw?.sizes?.find((s) => s.label === selectedSize)?.unitAmount ??
    null;
  const currency =
    productRaw?.sizes?.find((s) => s.label === selectedSize)?.currency ?? "cad";

  return (
    <Container sx={{ py: 4 }}>
      <Grid container spacing={4} alignItems="center">
        {/* Image on Left */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            component="img"
            src={product.image || "/images/placeholder.jpg"}
            alt={product.name}
            sx={{
              width: "100%",
              maxHeight: 400,
              objectFit: "contain",
              borderRadius: 2,
              boxShadow: 2,
            }}
          />
        </Grid>

        {/* Details on Right */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
            {product.name}
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            {product.description}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold" }}>Size:</Typography>
            <Select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              size="small"
              sx={{ mt: 1, minWidth: 160 }}
            >
              {product.sizes.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
            {/* Prefer your legacy map if present; otherwise compute from cents */}
            {product.price?.[selectedSize]
              ? `$${product.price[selectedSize]} (${selectedSize})`
              : `${toMoney(unitAmountCents, currency)} (${selectedSize})`}
          </Typography>

          {/* Heat badge is available via product.spiceLevel if you want:
              <Typography sx={{ mb: 2 }}>
                🔥 Heat Level: {product.spiceLevel}
              </Typography>
          */}

          <Button
            variant="contained"
            onClick={handleAddToCart}
            sx={{
              backgroundColor: "#fbb033",
              color: "#000",
              fontWeight: "bold",
            }}
          >
            Add to Cart
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
}
